/**
 * InstructorConfirmationPill — pupil-side calm read-only pill that
 * surfaces the instructor's 1-tap decision on a lesson review
 * (Track A, Phase F, Feb 13, 2026).
 *
 * Source of truth: lesson_review.instructor_confirmation (mirrored
 * by ai_offers.confirm endpoint — no new endpoint needed).
 *
 * Operator-locked copy (NEVER punishment/failure framing):
 *   progress  → "Move on to the next focus"
 *   repeat    → "Practise this again"
 *   step_back → "Strengthen the previous step"
 *   discuss   → "Talk this through with your instructor"
 *
 * The pill is invisible when the review carries no confirmation
 * (i.e. legacy reviews, non-Track-A flows, or offers still
 * awaiting the instructor's tap).
 */
import { CheckCircle2, RotateCcw, ArrowLeftCircle, MessageCircle } from "lucide-react";


const DECISION_META = {
  progress: {
    label: "Move on to the next focus",
    icon: CheckCircle2,
    tone: "border-emerald-700/40 bg-emerald-900/15 text-emerald-100",
    badgeTone: "text-emerald-300",
  },
  repeat: {
    label: "Practise this again",
    icon: RotateCcw,
    tone: "border-amber-700/40 bg-amber-900/15 text-amber-100",
    badgeTone: "text-amber-300",
  },
  step_back: {
    label: "Strengthen the previous step",
    icon: ArrowLeftCircle,
    tone: "border-sky-700/40 bg-sky-900/15 text-sky-100",
    badgeTone: "text-sky-300",
  },
  discuss: {
    label: "Talk this through with your instructor",
    icon: MessageCircle,
    tone: "border-violet-700/40 bg-violet-900/15 text-violet-100",
    badgeTone: "text-violet-300",
  },
};


export default function InstructorConfirmationPill({ confirmation }) {
  // Strict shape guard — render nothing for any legacy / partial doc.
  const status = confirmation?.status;
  const decision = confirmation?.decision;
  if (status !== "confirmed" || !decision || !DECISION_META[decision]) {
    return null;
  }
  const meta = DECISION_META[decision];
  const Icon = meta.icon;
  const note = (confirmation.decision_note || "").trim();

  return (
    <section
      data-testid="lr-instructor-confirmation-pill"
      className={`rounded-2xl border p-4 ${meta.tone}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <p
          data-testid="lr-instructor-confirmation-badge"
          className={`text-[10px] uppercase tracking-widest font-extrabold ${meta.badgeTone}`}
        >
          What your instructor said
        </p>
      </div>
      <p
        data-testid="lr-instructor-confirmation-decision"
        className="text-[14px] font-extrabold mt-1.5 leading-snug"
      >
        {meta.label}
      </p>
      {note && (
        <p
          data-testid="lr-instructor-confirmation-note"
          className="text-[12px] opacity-85 leading-relaxed mt-1.5 italic"
        >
          "{note}"
        </p>
      )}
    </section>
  );
}
